import { initSvdCheckoutGuarantee } from "./eu-legal-guarantee-checkout"
import { initProductDescriptionAccordion } from "./product-description-accordion"

// The shared wrapper still injects a site-wide floating QR for this store.
// SVD only shows that QR inside the cart and checkout view.
function removeEuLegalGuarantee() {
  document.querySelectorAll(".reach-eu-legal-guarantee").forEach((node) => node.remove())
}

removeEuLegalGuarantee()
new MutationObserver(removeEuLegalGuarantee).observe(document.documentElement, {
  childList: true,
  subtree: true,
})

function loadReachWrapperScript(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const inject = () => {
      const script = document.createElement("script")
      script.src = url
      script.onload = () => resolve()
      script.setAttribute("id", "reach-init-script")
      script.onerror = (error) => reject(error)
      const parent = document.body ?? document.head ?? document.documentElement
      parent.appendChild(script)
    }

    // svd.se includes this file from <head>, so document.body is still null.
    // The store node and xReachStore("id=store") are parsed later. Wait until
    // that call has been queued, then inject widget.js.
    if (document.body) {
      inject()
    } else {
      document.addEventListener("DOMContentLoaded", inject, { once: true })
    }
  })
}

let scriptLoaded = false
// Then load external script
// loadReachWrapperScript("http://localhost:1234/widget.js?95806261")
loadReachWrapperScript("https://store.loyative.com/widget.js?95806261")
  .then(() => {
    scriptLoaded = true
    pendingXReachStoreCalls.forEach(({ args, resolve, reject }, index) => {
      try {
        const result = window.xReachStore?.(...args)
        const id = extractIdFromParams(...args)
        injectReachToolsPanelContainer(id)
        injectPurchaseCancellationFooter()
        window.xReachCategories?.(`id=reach--categories`)
        window.xReachMiniCart?.(`id=reach--mini-cart`)
        resolve(result)
      } catch (error) {
        reject(error)
      }
    })
    initProductDescriptionAccordion()
    initSvdCheckoutGuarantee()
    // Continue with code that depends on the loaded script
  })
  .catch((error) => {
    console.error("Failed to load script:", error)
  })

interface PendingCall {
  args: any[]
  resolve: (result: any) => void
  reject: (error: any) => void
}

// Create a queue to store calls before the script is loaded
const pendingXReachStoreCalls: PendingCall[] = []
let originalXProductBrowser: Function | null = null

// Proxy the xProductBrowser function
;(window as any).xReachStore = (...args: any[]) => {
  return new Promise((resolve, reject) => {
    pendingXReachStoreCalls.push({ args, resolve, reject })
  })
}

function injectReachToolsPanelContainer(id: string) {
  const reachToolsPanelDiv = document.createElement("div")
  reachToolsPanelDiv.setAttribute("id", "reach--tools-panel")
  reachToolsPanelDiv.style.visibility = "hidden"
  reachToolsPanelDiv.style.display = "flex"
  reachToolsPanelDiv.style.justifyContent = "space-between"
  reachToolsPanelDiv.style.alignItems = "flex-start"
  reachToolsPanelDiv.style.width = "100%"
  reachToolsPanelDiv.style.marginBottom = "0.5em"
  reachToolsPanelDiv.style.gap = "24px"

  reachToolsPanelDiv.classList.add("reach--tools-panel")

  const categoriesDiv = document.createElement("div")
  categoriesDiv.setAttribute("id", "reach--categories")
  categoriesDiv.style.flexGrow = "1"

  const reachMiniCartDiv = document.createElement("div")
  reachMiniCartDiv.setAttribute("id", "reach--mini-cart")

  reachToolsPanelDiv.appendChild(categoriesDiv)
  reachToolsPanelDiv.appendChild(reachMiniCartDiv)

  document.querySelector(`#${id}`)?.prepend(reachToolsPanelDiv)
}

function injectPurchaseCancellationFooter() {
  if (document.querySelector(".reach-purchase-cancellation-footer") != null) {
    return
  }

  const footer = document.createElement("p")
  footer.className = "reach-purchase-cancellation-footer"
  footer.innerHTML =
    'Ångrar du ditt köp, <a href="https://www.loyative.com/regret">klicka här</a>.'

  document.body.appendChild(footer)
}

function extractIdFromParams(...args: any[]) {
  const id = args
    .map((arg) => {
      if (typeof arg !== "string") {
        return undefined
      }

      const possiblyId = arg.split("id=")[1].split("&")[0]
      return possiblyId
    })
    .filter((x) => x != null)[0]

  return id
}
